import {
    useMemo,
    useEffect,
    useState,
    type ReactElement,
    ForwardedRef,
    forwardRef,
    useRef,
    RefObject,
    CSSProperties,
    type FocusEvent,
} from 'react';
import classnames from 'classnames';

import { DropBase, type Placement } from '@hh.ru/magritte-internal-drop-base';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { DropContainer } from '@hh.ru/magritte-ui-drop/DropContainer';
import { PureChildren } from '@hh.ru/magritte-ui-drop/PureChildren';
import type { DropProps, DropPlacement } from '@hh.ru/magritte-ui-drop/types';
import { Layer } from '@hh.ru/magritte-ui-layer';

import styles from './drop.less';

const PLACEMENTS_LIST: Placement[] = [
    'left-top',
    'left-bottom',
    'right-top',
    'right-bottom',
    'top-left',
    'top-right',
    'bottom-left',
    'bottom-right',
];

const isKeyboardInput = (element: HTMLElement): boolean => {
    const tagName = element.tagName.toLowerCase();
    const keyboardInputTypes = ['text', 'password', 'email', 'number', 'tel', 'url', 'search'];
    return (
        (tagName === 'input' && keyboardInputTypes.includes((element as HTMLInputElement).type)) ||
        tagName === 'textarea' ||
        element.hasAttribute('contenteditable')
    );
};

const DropComponent = (
    {
        visible,
        activatorRef,
        onClose,
        maxWidth = 320,
        minHeight = 200,
        width,
        space = 600,
        children,
        footer,
        role = 'dialog',
        dividerState = 'show',
        widthEqualToActivator = false,
        allowShrinkHeightToFitIntoViewport = true,
        autoFocusWhenOpened,
        closeByClickOutside = true,
        'data-qa': dataQA = 'drop',
        onAppear,
        onBeforeExit,
        onAfterExit,
        placement,
        forcePlacement,
        ignoreIntersections,
        lockPlacement = false,
        ...props
    }: DropProps,
    ref: ForwardedRef<HTMLElement>
): ReactElement | null => {
    const { isMobile } = useBreakpoint();
    const [animationTimeout, setAnimationTimeout] = useState(200);
    const [isPlacementLockedByFocus, setPlacementLockedByFocus] = useState(false);
    const forceUpdateDropMetricsRef = useRef<VoidFunction>();

    const actualIgnoreIntersections =
        ignoreIntersections === undefined ? !allowShrinkHeightToFitIntoViewport : ignoreIntersections;

    const placementsList = useMemo(() => {
        const placementsArray: DropPlacement[] = Array.isArray(placement) ? placement : [placement];
        return (
            forcePlacement
                ? placementsArray
                : [...placementsArray, ...PLACEMENTS_LIST.filter((place) => !placement.includes(place))]
        ) as Placement[];
    }, [placement, forcePlacement]);

    useEffect(() => {
        const animationTimeoutElement = document.createElement('div');
        animationTimeoutElement.classList.add(styles.dropAnimationTimeout);
        document.body.appendChild(animationTimeoutElement);
        const style = window.getComputedStyle(animationTimeoutElement);
        const animationTimeout = parseInt(style.getPropertyValue(`--animation-duration`), 10);
        if (Number.isInteger(animationTimeout)) {
            setAnimationTimeout(animationTimeout);
        }
        document.body.removeChild(animationTimeoutElement);
    }, []);

    useEffect(() => {
        forceUpdateDropMetricsRef.current?.();
    }, [maxWidth, space, footer]);

    const focusHandler = (event: Event | FocusEvent) => {
        const target = event.target;
        if (!target || !isKeyboardInput(target as HTMLElement)) {
            return;
        }
        setPlacementLockedByFocus(true);
    };
    const blurHandler = () => setPlacementLockedByFocus(false);

    useEffect(() => {
        if (!visible || isMobile || !activatorRef.current) {
            return void 0;
        }

        const activatorElement = activatorRef.current;
        activatorElement.addEventListener('focus', focusHandler);
        activatorElement.addEventListener('blur', blurHandler);
        return () => {
            activatorElement.removeEventListener('focus', focusHandler);
            activatorElement.removeEventListener('blur', blurHandler);
        };
    }, [visible, activatorRef, isMobile]);

    if (isMobile) {
        return null;
    }

    let widthStyle: CSSProperties;
    if (widthEqualToActivator) {
        widthStyle = { width: '100%' };
    } else {
        widthStyle = width ? { width } : { maxWidth };
    }

    return (
        <Layer layer="drop">
            <DropBase
                {...props}
                visible={visible}
                placement={placementsList}
                activatorRef={activatorRef}
                closeByClickOutside={closeByClickOutside}
                onClose={onClose}
                autoFocusWhenOpened={autoFocusWhenOpened ?? role !== 'status'}
                animationClassNames={{
                    enter: styles.dropEnter,
                    enterActive: styles.dropEnterActive,
                    exit: styles.dropExit,
                    exitActive: styles.dropExitActive,
                }}
                animationTimeout={animationTimeout}
                widthEqualToActivator={widthEqualToActivator}
                allowShrinkHeightToFitIntoViewport={allowShrinkHeightToFitIntoViewport}
                ignoreIntersections={actualIgnoreIntersections}
                minHeight={minHeight}
                ref={ref}
                onAppear={onAppear}
                onBeforeExit={onBeforeExit}
                onAfterExit={onAfterExit}
                forceDropMetricsUpdate={forceUpdateDropMetricsRef as RefObject<VoidFunction>}
                lockPlacement={isPlacementLockedByFocus || lockPlacement}
            >
                {(resizeRefCallbackFactory) => (
                    <div
                        className={classnames(styles.drop, {
                            [styles.dropSpace300]: space === 300,
                            [styles.dropSpace400]: space === 400,
                            [styles.dropSpace600]: space === 600,
                            [styles.dropWithFooter]: !!footer,
                        })}
                        style={widthStyle}
                        data-qa={dataQA}
                        key="drop-outer-container"
                        onFocus={focusHandler}
                        onBlur={blurHandler}
                    >
                        <DropContainer
                            key="drop-container"
                            dividerState={!footer ? 'hide' : dividerState}
                            ref={resizeRefCallbackFactory('scroll-container', false, true)}
                        >
                            <div ref={resizeRefCallbackFactory('drop-content')}>
                                <PureChildren>{children}</PureChildren>
                            </div>
                        </DropContainer>
                        {footer && (
                            <div ref={resizeRefCallbackFactory('drop-footer')} className={styles.dropFooter}>
                                <PureChildren>{footer}</PureChildren>
                            </div>
                        )}
                    </div>
                )}
            </DropBase>
        </Layer>
    );
};

const Drop = forwardRef<HTMLElement, DropProps>(DropComponent);
export { Drop };
